/**
 * FamilyShareManage — patient-side: who can see my status?
 *
 * Minimal screen for patients only. Lists active authorizations + one
 * input to invite a caregiver by email/name (via /family/caregivers/search).
 * Revoke at any time.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Trash2, Search, Loader2, ArrowLeft, UserPlus, Lock, ExternalLink,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrgEntitlements } from '../hooks/useOrgEntitlements';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FamilyShareManage() {
  const { loading: entLoading, hasFlag } = useOrgEntitlements();
  if (entLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!hasFlag('family_view')) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6"
            data-testid="family-share-locked">
        <div className="max-w-xl text-center space-y-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100">
            <Lock className="w-7 h-7 text-slate-500" />
          </div>
          <h1 className="text-3xl font-semibold text-slate-900">
            Family share isn&apos;t enabled here yet
          </h1>
          <p className="text-base text-slate-600 leading-relaxed">
            Family share lets your loved ones see your upcoming visits and
            care team — securely and only for what you approve. You can
            manage every invite yourself.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            {/* Iter 163 · Patient viewing this page gets a direct route
                to the Family Portal instead of just an Ops email. */}
            <Link to="/patient/family"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
                data-testid="family-share-open-portal">
              Open Family Portal
            </Link>
            <a href="mailto:ops@caraloom.com?subject=Enable%20Family%20View"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50">
              <ExternalLink className="w-4 h-4" /> Email Ops
            </a>
          </div>
        </div>
      </div>
    );
  }
  return <ShareInner />;
}

function ShareInner() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [auths, setAuths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [relationship, setRelationship] = useState('');
  const [includeAcp, setIncludeAcp] = useState(false);

  // Effect for non-patient redirect (must run before any conditional return)
  useEffect(() => {
    if (user && user.role !== 'patient') {
      navigate('/family');
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/family/authorizations`,
        { headers: { Authorization: `Bearer ${token}` } });
      setAuths(res.data.rows || []);
    } catch {
      toast.error('Failed to load authorizations');
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const search = useCallback(async (qq) => {
    if (!qq || qq.length < 3) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(
        `${API}/family/caregivers/search?q=${encodeURIComponent(qq)}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setResults(res.data.results || []);
    } catch {
      // ignore
    } finally {
      setSearching(false);
    }
  }, [token]);

  const grant = async (caregiverId) => {
    try {
      await axios.post(`${API}/family/authorizations`,
        { caregiver_id: caregiverId,
          scope: includeAcp ? ['status', 'acp'] : ['status'],
          relationship: relationship || null },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Access granted');
      setQ('');
      setResults([]);
      setRelationship('');
      setIncludeAcp(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Grant failed');
    }
  };

  const revoke = async (authId) => {
    if (!confirm('Revoke this person\'s access?')) return;
    try {
      await axios.post(`${API}/family/authorizations/${authId}/revoke`,
        { reason: 'patient_revoked' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Access revoked');
      load();
    } catch {
      toast.error('Revoke failed');
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-8" data-testid="family-share-manage">
      <Link to="/family"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            data-testid="back-to-family-view">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Who can see your status</h1>
            <p className="text-sm text-slate-500 mt-1">
              You decide. Revoke any time.
            </p>
          </div>
        </div>
      </div>

      {/* Invite */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-slate-600" /> Add someone
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Input value={q}
                    onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
                    placeholder="Search by email or name (3+ chars)…"
                    data-testid="caregiver-search-input" />
          </div>
          <Input value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  placeholder="Relationship (Spouse, Child…)"
                  data-testid="relationship-input" />
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer"
                data-testid="acp-scope-toggle">
          <input type="checkbox" checked={includeAcp}
                  onChange={(e) => setIncludeAcp(e.target.checked)}
                  className="mt-0.5"
                  data-testid="acp-scope-checkbox" />
          <span>
            Also share my <span className="font-medium">advance-care-planning checklist</span> (read-only)
            <span className="block text-xs text-slate-500">
              They&apos;ll see which decisions are documented — never the details, never editable.
            </span>
          </span>
        </label>

        {/* Search results */}
        {searching ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Searching…
          </div>
        ) : results.length > 0 ? (
          <ul className="space-y-2" data-testid="caregiver-search-results">
            {results.map((r) => (
              <li key={r.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:bg-slate-50">
                <div>
                  <div className="text-sm font-medium text-slate-900">{r.display_name}</div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                </div>
                <button onClick={() => grant(r.id)}
                        className="text-xs px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800"
                        data-testid={`grant-btn-${r.id}`}>
                  Authorize
                </button>
              </li>
            ))}
          </ul>
        ) : q.length >= 3 ? (
          <p className="text-sm text-slate-500">
            No matches. They need to register on CaraLoom first.
          </p>
        ) : null}
      </div>

      {/* Active authorizations */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Active access</h2>
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : auths.length === 0 ? (
          <p className="text-sm text-slate-500">
            No one is currently authorized.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="active-authorizations">
            {auths.map((a) => (
              <li key={a.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    {a.relationship || 'Authorized contact'}
                  </div>
                  <div className="text-xs text-slate-500">
                    Granted {new Date(a.granted_at).toLocaleDateString()}
                    {' · '}
                    Scope: {a.scope.join(', ')}
                  </div>
                </div>
                <button onClick={() => revoke(a.id)}
                        className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 inline-flex items-center gap-1"
                        data-testid={`revoke-btn-${a.id}`}>
                  <Trash2 className="w-3 h-3" /> Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
